import { FlexBox, Icon, Spinner } from '@dixa/lib-shared-habla';
import {
  imageUploadActions,
  imageUploadState,
} from 'articleEditor/hooks/imageUpload/imageUpload.store';
import { UseFloatingMenuReturn } from 'articleEditor/hooks/useFloatingMenu';
import { basicBlockActions } from 'articleEditor/utils/actions';
import cx from 'classnames';
import { useEditableProps } from 'lib-knowledge-document-structure/plate/common';
import { useCallback, useMemo } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { useSnapshot } from 'valtio';

import { SortableBlockWrapper } from '../baseBlock';
import s from '../baseBlock/block.module.scss';

import { ImageBlockProps } from './imageBlock';

type ImageBlockPlaceholderProps = ImageBlockProps & {
  floatingUploadMenu: UseFloatingMenuReturn;
};

export default function ImageBlockPlaceholder({
  attributes,
  element,
  floatingUploadMenu,
  children,
}: ImageBlockPlaceholderProps): JSX.Element {
  const blockActions = useMemo(() => [...basicBlockActions], []);
  const { readOnly } = useEditableProps();
  const { statusByBlockId } = useSnapshot(imageUploadState);
  const uploadStatus = statusByBlockId[element.id];

  const handleClick = useCallback(() => {
    imageUploadActions.clear(element.id);
    floatingUploadMenu.openFloatingMenu();
  }, [element.id, floatingUploadMenu]);

  const placeholder = useMemo(() => {
    if (uploadStatus === 'uploading') {
      return (
        <FlexBox alignItems="center" gap="200">
          <Spinner size={20} />
          Uploading image...
        </FlexBox>
      );
    }

    if (uploadStatus === 'failed') {
      return (
        <FlexBox alignItems="center">
          <Icon
            className={s['mediaUploadAreaIcon']}
            name="exclamation-circle-filled"
          />
          Image could not be uploaded. Click to try again.
        </FlexBox>
      );
    }

    return (
      <FlexBox alignItems="center">
        <Icon className={s['mediaUploadAreaIcon']} name="image" />
        Embed or upload an image...
      </FlexBox>
    );
  }, [uploadStatus]);

  if (readOnly) {
    return <></>;
  }

  return (
    <SortableBlockWrapper
      className={s['blockImagePlaceholder']}
      element={element}
      blockActions={blockActions}
    >
      <div
        {...floatingUploadMenu.floatingMenuGetReferenceProps({
          ...attributes,
          ref: mergeRefs([
            floatingUploadMenu.floatingResult.refs.setReference,
            attributes.ref,
          ]),
          className: cx(s['mediaUploadArea'], s['blockContent']),
          contentEditable: false,
          onClick: uploadStatus === 'uploading' ? undefined : handleClick,
        })}
      >
        {placeholder}
      </div>
      {children}
    </SortableBlockWrapper>
  );
}
